import { useTranslation } from 'react-i18next';
import React, { memo, useMemo, useCallback } from 'react';
import cx from 'clsx';
import Skeleton from 'react-loading-skeleton';
import { useHistory } from 'react-router-dom';

import { IconRcFeedRepost, IconRcFeedPined, IconRcClose } from '@/assets/stream';
import {
  UnionAvatar,
  HelperTooltip,
  CompatibleEntityContent,
  UserCardPopup,
  UnionFollowButtonV2,
  LazyMediaSized,
  VipLogoWithGift,
} from '@/components';
import { MobileModalTip } from '@/components/shared/MobileModalTip';
import { getOfficialPath, isOfficalAddress, openNewWindow, useCurrentUserId } from '@/utils';
import { useDeviceTypeIsMobile, useSwitch, useImagePreview } from '@/hooks';
import { OfficialAccountVerifyStatus } from '@/hooks/useOfficialAccount';

import { setArticleFromList } from '../../DesktopContainer';
import { FeedArticleDetail, FeedItem } from '../../types';
import { ArticleContent } from '../ArticleContent';
import { StreamUserListModal } from '../OperationUserListModal';
import { FeedMoreAction } from '../FeedMoreAction';
import style from './index.module.scss';
import { SuicideTag } from '@/views/Profile/components/SuicideTag';
import { PrimePostVoteBtn } from '../PrimePostVoteBtn';
import { HideStreamCard } from '../HideStreamCard';
import { useHidePost } from '../../hooks';
import { message } from 'antd';

type FeedListItemProps = {
  className?: string;
  feed: FeedArticleDetail;
  showMenu?: boolean;
  showPinned?: boolean;
  sharedByGroupMember?: UnionAccount;
  enableReadFeedback?: boolean;
  enableContributorId?: boolean;
  restParams?: Record<string, string | number | boolean | undefined>;
  openDetailInSelf?: boolean;
  disableBorderBottom?: boolean;
  hideGroupPinMenu?: boolean;
  showVoteBtn?: boolean;
  isInChannel?: boolean;
  showHideBtn?: boolean;
};

export const FeedListItem = memo((props: FeedListItemProps) => {
  const { t } = useTranslation();
  const address = useCurrentUserId();
  const {
    className,
    feed,
    showMenu = true,
    showPinned,
    enableReadFeedback,
    enableContributorId,
    restParams,
    openDetailInSelf,
    sharedByGroupMember,
    disableBorderBottom,
    hideGroupPinMenu,
    showVoteBtn,
    isInChannel,
    showHideBtn,
  } = props;
  const isMobile = useDeviceTypeIsMobile();
  const history = useHistory();
  const { handlePreview } = useImagePreview();
  const { mutate: hidePost } = useHidePost(feed?.article?.id);
  const isHidden = feed?.article?._isHidden;
  const isPostOwner = feed?.article?.creator_id === address;
  const isOfficial = isOfficalAddress(feed?.article?.creator_id);
  const disableAuthorPopup =
    isOfficial &&
    (feed?.article?.creator as OfficialAccount)?.verify_status ===
      OfficialAccountVerifyStatus.INACTIVE;

  const repostInfo = useMemo(() => {
    const repostCount = (feed as FeedItem).repost_list?.length || 0;

    return repostCount
      ? `${(feed as FeedItem)
          .repost_list!.slice(0, 2)
          .map(x => x._author)
          .join(', ')} ${t('FeedListItem.reposted', {
          count: repostCount > 2 ? repostCount - 2 : 0,
        })}`
      : '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(feed as FeedItem).repost_list]);

  const {
    on: onOperationUserListModal,
    turnOn: showOperationUserListModal,
    turnOff: hideOperationUserListModal,
  } = useSwitch();

  const { on: mobileTip, turnOn: showMobileTip, turn: setMobileTip } = useSwitch();

  const handleContentClick = useCallback(
    ({ newTab, qs = '' }: { newTab?: boolean; qs?: string } = { newTab: false, qs: '' }) => {
      // prevent click while selecting text
      if (window.getSelection()?.toString()?.length) {
        return;
      }

      if (isHidden) {
        return;
      }

      if (!openDetailInSelf || newTab) {
        openNewWindow(`/stream/${feed.article.id}${qs}`, '_blank');

        return;
      }

      if (isMobile) {
        history.push(`/stream/${feed.article.id}${qs}`);

        return;
      }

      // openSteamDetailModal({
      //   id: feed.article.id,
      //   article: feed,
      // });

      setArticleFromList(feed);
      history.push(`/stream/${feed.article.id}${qs}`);
    },
    [feed, history, isHidden, isMobile, openDetailInSelf],
  );

  const handlePromotionClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();

      handleContentClick({ qs: '?mode=promotion' });
    },
    [handleContentClick],
  );

  const handleMiddleClick = useCallback(
    (e: React.MouseEvent) => {
      // 实现延迟阅读的效果，使用鼠标滚轮点击时在新 Tab 打开并且不 focus 过去
      // 这样可以在不影响继续刷 feed 的同时把想看的内容打开
      if (e.button === 1) {
        handleContentClick({ newTab: true });
      }
    },
    [handleContentClick],
  );

  const handleImageClick = useCallback(
    (e: React.MouseEvent<HTMLImageElement>) => {
      handlePreview(e);
      e.stopPropagation();
    },
    [handlePreview],
  );

  const handleUserClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      const userId = e.currentTarget?.dataset?.userId || feed.article.creator_id;
      const isOffical = isOfficalAddress(userId);
      // path上officialId不带0a；

      openNewWindow(
        isOffical
          ? `${getOfficialPath(feed?.article?.creator as OfficialAccount)}/stream`
          : `/profile/${userId}/stream`,
        '_blank',
      );
    },
    [feed],
  );

  const handleRepostListClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (isMobile) {
        showMobileTip();
        return;
      }
      showOperationUserListModal();
    },
    [showMobileTip, showOperationUserListModal, isMobile],
  );

  const reposters = useMemo(() => (feed as FeedItem)?.repost_list?.map(x => x.user), [feed]);
  const isPined =
    showPinned && (isInChannel ? feed?.article?.is_channel_pinned : feed?.article?.is_pinned);

  const handleClickHide = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (isPostOwner) {
        message.error(t('FeedListItem.disableHideTip'));
        return;
      }
      hidePost();
    },
    [hidePost, isPostOwner, t],
  );

  return (
    <>
      <div
        className={cx(
          style.streamListItem,
          feed.article.is_withdrawn && style.isWithdraw,
          disableBorderBottom && style.disableBorderBottom,
          isHidden && style.isHidden,
          className,
        )}
        onClick={() => handleContentClick()}
        onMouseUp={handleMiddleClick}>
        <div className={cx(style.avatarWrap, isHidden && style.isHidden)}>
          {repostInfo ? (
            <div className={style.statusIcon}>
              <IconRcFeedRepost className={style.opIcon} />
            </div>
          ) : null}
          {isPined ? (
            <div className={style.statusIcon}>
              <IconRcFeedPined className={style.opIcon} />
            </div>
          ) : null}
          <div onClick={feed.article._isBot ? undefined : handleUserClick}>
            <UnionAvatar
              size={isMobile ? 38 : 48}
              data={feed.article.creator}
              isBot={feed.article._isBot}
              isBotActive={feed.article.monitor_alert?.bot?.is_active}
              className={cx(
                feed.article._isBot &&
                  !feed.article.monitor_alert?.bot?.is_active &&
                  style.disableAvatarLink,
              )}
              officialLogoOption={{
                placement: 'topLeft',
                align: { targetOffset: [33, 0] },
                tooltipClassName: style.officialVerifyTip,
              }}
              userCardPopup={{
                restParams,
                disabled: disableAuthorPopup,
              }}
            />
          </div>
          {showVoteBtn && feed?.prime_post_issue ? <PrimePostVoteBtn feed={feed} /> : null}
        </div>
        <div className={style.streamMain}>
          <div className={cx(style.streamHeadContainer, isHidden && style.isHidden)}>
            {repostInfo ? (
              <div className={style.statusInfo}>
                <span className={style.repostInfo} onClick={handleRepostListClick}>
                  {repostInfo}
                </span>
              </div>
            ) : null}
            {isPined ? <div className={style.statusInfo}>{t('FeedListItem.17m65l4')}</div> : null}
            <div className={style.streamHead}>
              <div className={style.creator}>
                <div className={style.left}>
                  {feed?.article?._isBot ? (
                    <div
                      className={cx(
                        style.uid,
                        !feed?.article?.monitor_alert?.bot?.is_active && style.isOffline,
                      )}>
                      {feed.article._botName}
                    </div>
                  ) : (
                    <UserCardPopup
                      restParams={restParams}
                      address={feed.article.creator?.id || ''}
                      disabled={disableAuthorPopup}>
                      <div onClick={handleUserClick} className={style.uid}>
                        {(feed.article.creator as UserInfo)?.is_suicide ? (
                          <SuicideTag userAddress={feed.article.creator?.id!} />
                        ) : (
                          feed.article._author
                        )}
                      </div>
                    </UserCardPopup>
                  )}
                  {feed.article._showVip ? (
                    <VipLogoWithGift userInfo={feed.article.creator as UserInfo} marginRight={8} />
                  ) : null}
                  <HelperTooltip title={feed.article._fulltime} placement="topLeft">
                    <div className={style.createdAt}>{`· ${feed.article._daytime}`}</div>
                  </HelperTooltip>
                  {feed.article.creator?.id ? (
                    <UnionFollowButtonV2
                      address={feed.article.creator?.id}
                      isFollowing={feed.article.creator?.is_following}
                      restParams={restParams}
                    />
                  ) : null}
                </div>
                <div className={style.right}>
                  {showMenu && !showHideBtn ? (
                    <div className={style.feedMoreActionContainer}>
                      <FeedMoreAction
                        articleDetail={feed}
                        hideGroupPin={hideGroupPinMenu}
                        isInChannel={isInChannel}
                      />
                    </div>
                  ) : null}
                  {showHideBtn && !isHidden ? (
                    <HelperTooltip
                      title={t('FeedListItem.hidTip')}
                      placement="topRight"
                      align={{ targetOffset: [-11, -5] }}>
                      <IconRcClose className={style.closeIcon} onClick={handleClickHide} />
                    </HelperTooltip>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className={style.streamContentContainer}>
            <div className={cx(style.streamContent, isHidden && style.isHidden)}>
              <ArticleContent
                articleDetail={feed}
                className={style.content}
                showRawImage
                maxDisplay={600}
                sharedByGroupMember={sharedByGroupMember}
                maxImgSize={isMobile ? [window.screen.width - 102, 260] : undefined}
                enableReadFeedback={enableReadFeedback}
                enableContributorId={enableContributorId}
                disableComment={!feed?.article?.has_permission}
                onPromotionClick={handlePromotionClick}
                showVoteBtn={showVoteBtn && !!feed?.prime_post_issue}
              />
              {feed.comments?.length ? (
                <div className={style.commentsWrap}>
                  <div className={style.commentsTitle}>{t('FeedListItem.1hxfkng')}</div>
                  <div className={style.comments}>
                    {feed.comments?.map((x, i) => (
                      <div className={style.comment} key={x.id}>
                        <div
                          className={style.commentAuthor}
                          onClick={handleUserClick}
                          data-user-id={x.creator?.id}>
                          {x._author}:
                        </div>
                        {feed.article.is_visible ? (
                          <div className={style.commentComment}>
                            {x._replyTo ? (
                              <span className={style.contentReply}>
                                {t('FeedListItem.mx9569')}
                                {x._replyTo}:{' '}
                              </span>
                            ) : null}
                            <CompatibleEntityContent
                              content={x.content}
                              entities={x.entities}
                              content_translation={x.content_translation}
                              entities_translation={x.entities_translation}
                              creator_id={x.creator_id}
                              contentType="comment"
                            />
                            {x.images?.map((x, i) => (
                              <LazyMediaSized
                                className={style.commentImage}
                                src={x.raw.full_url}
                                onClick={handleImageClick}
                                key={i}
                                maxHeight={100}
                                maxWidth={isMobile ? window.screen.width - 102 : 200}
                                height={x.raw.height}
                                width={x.raw.width}
                              />
                            ))}
                          </div>
                        ) : (
                          <div className={style.invisibleComment} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
            {isHidden ? (
              <HideStreamCard className={style.hideSteamCard} article={feed.article} />
            ) : null}
          </div>
        </div>
      </div>

      <StreamUserListModal
        on={onOperationUserListModal}
        title={t('FeedListItem.1185kd')}
        turnOff={hideOperationUserListModal}
        users={reposters}
      />
      <MobileModalTip visible={mobileTip} setVisible={setMobileTip} />
    </>
  );
});

export const StreamListItemSkeleton = memo(() => {
  return (
    <div className={style.streamListItem}>
      <Skeleton width={48} height={48} circle className={style.avatarWrap} />
      <div className={style.streamMain}>
        <div className={style.streamHead}>
          <div className={style.creator}>
            <Skeleton className={style.uid} width={70} />
            <Skeleton className={style.createdAt} width={50} />
          </div>
        </div>
        <Skeleton className={style.content} width="100%" height={50} />
      </div>
    </div>
  );
});

export const LoadingSkeleton = memo(() => (
  <div className={style.loadingList}>
    {Array(6)
      .fill(0)
      .map((_, index) => (
        <StreamListItemSkeleton key={index} />
      ))}
  </div>
));
